<template>
  <div class="px-3 px-md-4 pb-5 immersive-dashboard">
    <b-row class="mb-4 align-items-center">
      <b-col>
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div class="hud-logo-pulse mr-3">
              <font-awesome-icon :icon="['far', 'warehouse']" size="lg" class="text-white" />
            </div>
            <div>
              <h3 class="font-weight-bold text-dark m-0 hud-title">Inicio</h3>
              <p class="text-muted m-0 fs-13">Módulos y funcionalidades principales del sistema</p>
            </div>
          </div>
          <div>
            <CategoryFilterBar
              :categories="groupedTools"
              :total="permittedTools.length"
              :value="activeCategoryNames"
              @input="setCategories"
            />
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <b-col lg="6" class="mb-3 mb-lg-0">
        <FavoritesCard
          :activeFavorites="activeFavorites"
          @navigate="navigate"
          @toggle-fav="toggleFavorite"
        />
      </b-col>
      <b-col lg="6">
        <div class="h-100" style="animation-delay: 0.1s;">
          <RecentVisitsCard />
        </div>
      </b-col>
    </b-row>

    <div class="categories-flow">
      <div
        v-for="(category, idx) in displayedCategories"
        :key="'cat-grid-' + category.name"
        class="category-item"
        :style="{ '--n': category.items.length, animationDelay: `${(idx + 1) * 0.05}s` }"
      >
        <SectionCard :headerTitle="category.name" :headerIcon="category.icon">
          <ToolCard
            v-for="tool in category.items"
            :key="tool.id"
            :tool="tool"
            :isFavorite="isFav(tool.id)"
            :canAddFavorite="canAddFavorite"
            @card-click="navigate(tool)"
            @toggle-fav="toggleFavorite(tool.id)"
          />
        </SectionCard>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import RecentVisitsCard from '../../shared/RecentVisitsCard.vue'
import FavoritesCard from './FavoritesCard.vue'
import ToolCard from './ToolCard.vue'
import SectionCard from './SectionCard.vue'
import CategoryFilterBar from './CategoryFilterBar.vue'

import CorredizoImg from 'images/corredizo.png'
import AbatibleImg from 'images/abatible.png'
import SeccionalImg from 'images/seccional.png'
import CortinaImg from 'images/cortina.png'
import AndenImg from 'images/anden.webp'
import PuertaAutomaticaImg from 'images/puerta_automatica.webp'
import ControlDeAccesoImg from 'images/access_control.webp'

export default {
  name: 'ProductionHome',
  components: {
    RecentVisitsCard,
    FavoritesCard,
    ToolCard,
    SectionCard,
    CategoryFilterBar
  },
  data () {
    return {
      processing: false
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser || {}
    }),
    ...mapGetters ([
      'canViewAccess',
      'isCoursesAdmin',
      'isCoursesMember',
      'isPurchaseDeliveryAdmin',
      'isPurchaseDeliverySupervisor'
    ]),
    categories () {
      const notNonProd = this.currentUser.role != 'non_prod'

      return [
        {
          order: 1,
          name: 'Clientes y Ventas',
          icon: ['far', 'address-book'],
          tools: [
            { id: 'clientes', title: 'Clientes', route: '/app/prod/clientes', icon: ['far', 'address-book'], visible: true },
            { id: 'cotizador', title: 'Cotizador', route: '/app/prod/cotizador', icon: ['fas', 'calculator'], visible: notNonProd },
            { id: 'proyectos', title: 'Proyectos', route: '/app/prod/proyectos', icon: ['far', 'briefcase'], visible: notNonProd },
            { id: 'cursos', title: 'Cursos clientes', route: '/app/prod/cursos', icon: ['fas', 'users-class'], visible: this.isCoursesAdmin || this.isCoursesMember },
            { id: 'venreg', title: 'Ven Reg 01', route: 'https://venreg.herokuapp.com/', icon: ['far', 'file-alt'], visible: true, isExternal: true }
          ]
        },
        {
          order: 2,
          name: 'Servicio',
          icon: ['fas', 'wrench'],
          tools: [
            { id: 'tickets', title: 'Sistema de tickets', route: '/app/tickets', icon: ['far', 'user-headset'], visible: true },
            { id: 'solicitudes_planos', title: 'Gestión de planos', route: '/app/prod/solicitudes-planos', icon: ['fas', 'ruler-triangle'], visible: true },
            { id: 'llamadas', title: 'Llamadas de servicio', route: '/app/prod/llamadas-servicio', icon: ['fas', 'tools'], visible: notNonProd },
            { id: 'pizarra', title: 'Pizarra Técnica', route: 'action:enterServiceModule', icon: ['fas', 'wrench'], visible: notNonProd }
          ]
        },
        {
          order: 4,
          name: 'Compras y Finanzas',
          icon: ['far', 'boxes-alt'],
          tools: [
            { id: 'soc', title: 'SOC/OC', route: '/app/prod/solicitud', icon: ['fas', 'hand-holding-box'], visible: notNonProd },
            { id: 'app_em', title: 'APP EM', route: '/app/prod/inventario/ingreso', icon: ['fas', 'dolly'], visible: this.isPurchaseDeliveryAdmin || this.isPurchaseDeliverySupervisor },
            { id: 'tarjetas', title: 'App Tarjetas', route: '/app/prod/transacciones', icon: ['far', 'credit-card'], visible: notNonProd }
          ]
        },
        {
          order: 5,
          name: 'Recursos Corporativos',
          icon: ['far', 'paperclip'],
          tools: [
            { id: 'centro_rec', title: 'Centro de recursos', route: '/app/prod/centro-recursos', icon: ['far', 'paperclip'], visible: notNonProd },
            { id: 'vacaciones', title: 'Solicitar vacaciones', route: 'https://accesospayroll.cmasap.com/SCGPayRollOne/', icon: ['far', 'umbrella-beach'], visible: true, isExternal: true },
            { id: 'academia', title: 'Academia accesos', route: 'https://acad.accesos.cr/login/index.php', icon: ['fas', 'user-graduate'], visible: true, isExternal: true }
          ]
        },
        {
          order: 6,
          name: 'Submitals',
          icon: ['far', 'warehouse'],
          tools: [
            { id: 'corredizos', title: 'Corredizos', route: '/app/prod/accesos/corredizos', img: CorredizoImg, visible: this.canViewAccess('Corredizo') },
            { id: 'abatibles', title: 'Abatibles', route: '/app/prod/accesos/abatibles', img: AbatibleImg, visible: this.canViewAccess('Abatible') },
            { id: 'seccionales', title: 'Seccionales', route: '/app/prod/accesos/seccionales', img: SeccionalImg, visible: this.canViewAccess('Seccional') },
            { id: 'cortinas', title: 'Cortinas', route: '/app/prod/accesos/cortinas', img: CortinaImg, visible: this.canViewAccess('Cortina') },
            { id: 'andenes', title: 'Andenes', route: '/app/prod/submitals/index/andenes-de-borde', img: AndenImg, visible: this.canViewAccess('AndenBorde') },
            { id: 'puertas', title: 'Puertas', route: '/app/prod/submitals/index/puertas-automaticas', img: PuertaAutomaticaImg, visible: this.canViewAccess('PuertaAutomatica') },
            { id: 'ca', title: 'Control Acceso', route: '/app/prod/submitals/index/controles-de-acceso', img: ControlDeAccesoImg, visible: this.canViewAccess('ControlAcceso') },
            { id: 'genericos', title: 'Genérico', route: '/app/prod/submitals/index/genericos', icon: ['far', 'globe'], visible: this.canViewAccess('Generico') }
          ]
        },
        {
          order: 7,
          name: 'Catálogos',
          icon: ['far', 'palette'],
          tools: [
            { id: 'inventario', title: 'Inventario', route: '/app/prod/inventario', icon: ['far', 'warehouse-alt'], visible: true },
            { id: 'disenos', title: 'Diseños', route: '/designs', icon: ['far', 'warehouse'], visible: notNonProd, railsRoute: true },
            { id: 'motores', title: 'Motores', route: '/motors', icon: ['fas', 'cogs'], visible: notNonProd, railsRoute: true },
            { id: 'colores', title: 'Colores', route: '/app/prod/colores', icon: ['far', 'palette'], visible: notNonProd }
          ]
        },
      ]
    },
    groupedTools () {
      return this.categories
        .map(c => ({ name: c.name, icon: c.icon, order: c.order, items: c.tools.filter(t => t.visible) }))
        .filter(c => c.items.length)
        .sort((a, b) => a.order - b.order)
    },
    permittedTools () {
      return this.groupedTools.flatMap(c => c.items)
    },
    allCategoryNames() {
      return this.groupedTools.map(g => g.name)
    },
    favorites () {
      return this.$store.getters['userPreferences/favoriteIds']('prod')
    },
    activeFavorites () {
      return this.favorites
        .map(id => this.permittedTools.find(s => s.id === id))
        .filter(Boolean)
    },
    canAddFavorite () {
      return this.favorites.length < this.$store.getters['userPreferences/maxFavorites']
    },
    activeCategoryNames () {
      const stored = this.$store.getters['userPreferences/selectedCategories']('prod')
      if (!stored) return this.allCategoryNames
      return stored.filter(n => this.allCategoryNames.includes(n))
    },
    displayedCategories () {
      return this.groupedTools.filter(c => this.activeCategoryNames.includes(c.name))
    }
  },
  methods: {
    setCategories (names) {
      this.$store.commit('userPreferences/setCategories', { scope: 'prod', names })
    },
    isFav (id) {
      return this.favorites.includes(id)
    },
    toggleFavorite (id) {
      this.$store.commit('userPreferences/toggleFavorite', { scope: 'prod', id })
    },
    navigate (tool) {
      if (tool.route === 'action:enterServiceModule') {
        this.enterServiceModule()
      } else if (tool.isExternal) {
        window.open(tool.route, '_blank', 'noopener')
      } else if (tool.railsRoute) {
        window.location.href = tool.route
      } else {
        this.$router.push(tool.route).catch(() => {})
      }
    },
    enterServiceModule () {
      if (this.processing) { return }
      this.processing = true
      this.$http.get('/api/service/tech_boards/verify_user')
        .then((res) => {
          if (res.data && res.data.length === 1) {
            window.location.href = `/servicio/pizarras/${res.data[0].id}`
          } else {
            window.location.href = '/servicio/pizarras'
          }
        })
        .catch(e => console.log(e))
        .finally(() => { this.processing = false })
    }
  }
}
</script>

<style scoped>
.immersive-dashboard {
  margin-top: 40px;
}

.hud-logo-pulse {
  width: 46px;
  height: 46px;
  background: linear-gradient(135deg, #163D7D 0%, #1c4e9c 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 20px rgba(22, 61, 125, 0.35);
}

.hud-title {
  letter-spacing: -0.5px;
}

.categories-flow {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.category-item {
  --item-w: 155px;
  --pad: 40px;
  flex: var(--n) 1 calc(var(--n) * var(--item-w) + var(--pad));
  min-width: 0;
}

.category-item > * {
  height: 100%;
}
</style>